import React, { useMemo, useCallback } from "react"

import { HighlightedQuery } from "src/components/HighlightedQuery"
import { SegmentPickerStore } from "src/components/SegmentPicker/store"

import { TRUNCATED_PATH_PLACEHOLDER } from "src/components/SegmentPicker/components/ListItemBreadcrumb/constants"
import { useStore } from "src/store/lib/useStore"
import { IBreadcrumbBuilderProps } from "src/components/SegmentPicker/components/ListItemBreadcrumb/sub-components/BreadcrumbBuilder/types"

/**
 * Component that builds breadcrumb content from path pairs
 */
export const BreadcrumbBuilder = ({
    pathPairs,
    segmentId,
    Component,
}: IBreadcrumbBuilderProps) => {
    const store = useStore(SegmentPickerStore)

    const createClickHandler = useCallback(
        (id: number) => async () => {
            await store.setParentId(id)
        },
        [store],
    )

    const renderTruncatedPlaceholder = (placeholder: string) => (
        <span key={placeholder}>{placeholder}</span>
    )

    const renderClickableItem = useCallback(
        (parent: number, path: string) => (
            <Component
                key={parent}
                onClick={createClickHandler(parent)}
                title={path}
            >
                <HighlightedQuery
                    text={path}
                    query={store.selectedSegments.meta.search}
                />
            </Component>
        ),
        [Component, createClickHandler, store.selectedSegments.meta.search],
    )

    const renderNonClickableItem = useCallback(
        (parent: number, path: string) => (
            <span key={parent} title={path}>
                <HighlightedQuery
                    text={path}
                    query={store.selectedSegments.meta.search}
                />
            </span>
        ),
        [store.selectedSegments.meta.search],
    )

    const breadcrumbItems = useMemo(() => {
        return pathPairs.map((pair) => {
            if (pair.path === TRUNCATED_PATH_PLACEHOLDER) {
                return renderTruncatedPlaceholder(TRUNCATED_PATH_PLACEHOLDER)
            }

            const parent =
                store.getAncestorAtIndex(segmentId, pair.index) ?? segmentId
            const parentSegment = store.getSegmentWithId(parent)

            if (
                Boolean(parentSegment) &&
                store.hasChildSegmentType(parentSegment)
            ) {
                return renderClickableItem(parent, pair.path)
            }

            return renderNonClickableItem(parent, pair.path)
        })
    }, [
        pathPairs,
        segmentId,
        store,
        renderClickableItem,
        renderNonClickableItem,
    ])

    return <>{breadcrumbItems}</>
}
